import { useState } from 'react';
import { ErrorBoundary as BaseErrorBoundary } from 'react-error-boundary';

import { IntentionNotSetModal, IntentionPromptSharedModal } from '@grow-therapy-team/seedling-components';

import { logger } from '../../datadog';
import { useTrackEvent } from '../../segment/segment';
import { EventTypeEnum } from '../../segment/types';
import { useSendSessionIntentionPromptCallback } from '../messages/useSendSessionIntentionPromptCallback';
import { IntentionSetModalWrapper } from './IntentionSetModal';
import { IntentionSettingButtonWrapper as IntentionSettingButton } from './IntentionSettingButton';
import { useIntentionState } from './useIntentionState';
import { IntentionUse } from './useIntentionUse';

function ErrorBoundary({ children }: { children: React.ReactNode }) {
  return <BaseErrorBoundary FallbackComponent={() => null}>{children}</BaseErrorBoundary>;
}

const IntentionPromptModal = ({
  isOpen,
  onClose,
  use,
  clientName,
  initials,
  providerImage,
  clientUserShortId,
  providerShortId,
  appointmentShortId,
  onSendPrompt,
}: {
  isOpen: boolean;
  onClose: () => void;
  use: IntentionUse;
  clientName: string;
  initials: string;
  providerImage: string;
  clientUserShortId: string;
  providerShortId: string;
  appointmentShortId: string;
  onSendPrompt: (appointmentShortId: string) => void;
}) => {
  switch (use) {
    case 'not-set':
    case 'not-set-active':
      return (
        <IntentionNotSetModal
          name={clientName}
          isOpen={isOpen}
          onClose={onClose}
          providerPhotoUrl={providerImage}
          onSendPrompt={() => onSendPrompt(appointmentShortId)}
        />
      );
    case 'shared':
      return (
        <IntentionPromptSharedModal
          isOpen={isOpen}
          onClose={onClose}
          name={clientName}
          providerPhotoUrl={providerImage}
          onResendPrompt={() => onSendPrompt(appointmentShortId)}
        />
      );
    case 'set':
      return (
        <IntentionSetModalWrapper
          isOpen={isOpen}
          onClose={onClose}
          clientName={clientName}
          initials={initials}
          clientUserShortId={clientUserShortId}
          providerShortId={providerShortId}
          appointmentShortId={appointmentShortId}
        />
      );
    default:
      return <></>;
  }
};

export function IntentionSettingWrapper() {
  const [isOpen, setIsOpen] = useState(false);

  const intentionState = useIntentionState();

  const sendSessionIntentionPrompt = useSendSessionIntentionPromptCallback();
  const { userClicked } = useTrackEvent();

  const handleSendPrompt = async (appointmentShortId: string) => {
    try {
      userClicked(EventTypeEnum.INTENTION_PROMPT_SENT, {
        appointmentShortId,
      });
      setIsOpen(false);
      await sendSessionIntentionPrompt(1);
    } catch (error) {
      logger.error('Error sending session intention prompt', { error });
    }
  };

  if (!intentionState || intentionState?.isLoading) {
    return null;
  }

  const { use, appointmentShortId, preferredName, initials, providerImage, clientUserShortId, providerShortId } =
    intentionState;

  return (
    <ErrorBoundary>
      <div className='absolute top-2 left-2'>
        <IntentionPromptModal
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          clientName={preferredName}
          initials={initials}
          providerImage={providerImage}
          use={use}
          clientUserShortId={clientUserShortId}
          providerShortId={providerShortId}
          appointmentShortId={appointmentShortId}
          onSendPrompt={handleSendPrompt}
        />
        <IntentionSettingButton
          use={use}
          onClick={() => setIsOpen(true)}
          clientName={preferredName}
          appointmentShortId={appointmentShortId}
        />
      </div>
    </ErrorBoundary>
  );
}
